import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "../context/AuthContexts";
import GlobalAlerts from "../components/GlobalAlerts";
import ImpersonationBanner from "../components/ImpersonationBanner";

import { authRoutes } from "../modules/auth/routes";
import { dashboardRoutes } from "../modules/dashboard/routes";
import { procurementRoutes } from "../modules/procurement/routes";
import { databankRoutes } from "../modules/databank/routes";
import { eventsRoutes, publicEventsRoutes } from "../modules/events/routes";
import { logbookRoutes } from "../modules/logbook/routes";
import { settingsRoutes } from "../modules/settings/routes";
import { accessRoutes } from "../modules/access/routes";
import { designationsRoutes } from "../modules/designation/routes";
import { comingSoonRoutes } from "../modules/comingSoon/routes";
import { departmentsRoutes } from "../modules/department/routes";
import { logbookDietRoutes } from "../modules/logbook-diets/routes";
import { executiveSetRoutes } from "../modules/executive-sets/routes";
import { executiveOfficeRoutes } from "../modules/executiveOffices/routes";
import { necOfficeRoutes } from "../modules/necOffice/routes";
import { chapterRoutes } from "../modules/chapter/routes";
import { adminsRoutes } from "../modules/admin/routes";
import { assessorRoutes } from "../modules/assessors/routes";
import { probationerRoutes } from "../modules/application/routes";
import { billingsRoutes } from "../modules/billing/routes";
import { membershipRoutes } from "../modules/membership/routes";
import { examRoutes } from "../modules/exam/routes";
import { hrRoutes } from "../modules/hr/routes";
import { storesRoutes } from "../modules/stores/routes";
import { committeesRoutes } from "../modules/committee/routes";
import { committeeReviewRoutes } from "../modules/committee-reviews/routes";
import { upgradeApplicationRoutes } from "../modules/upgrade-application/routes";
import { firmApplicationRoutes } from "../modules/firm-application/routes";
import { cpdRoutes } from "../modules/cpd/routes";
import { supportRoutes } from "../modules/support/routes";
import { mailRoutes } from "../modules/mail/routes";
import { nationalBodyRoutes } from "../modules/nationalBodies/routes";
import { forumRoutes } from "../modules/forum/routes";
import {
  graduatePledgeRoutes,
  publicGraduatePledgeRoutes,
} from "../modules/graduate-pledge/routes";

import UnauthorizedPage from "../modules/shared/pages/UnauthorizedPage";
import NotFoundPage from "../modules/shared/pages/NotFoundPage";
import AuthSync from "../components/AuthSync";

import RequirePermission from "../lib/permissionsGuard";
import OnboardingGuard from "../lib/onboardingGuard";
import GuestRedirect from "../lib/guestRedirect";
import MainLayout from "../modules/shared/layouts/MainLayout";
import ProfilePage from "../modules/auth/pages/ProfilePage";
import ProbationerApplicationWizard from "../modules/application/pages/ProbationerApplicationWizard";

const privateRoutes = [
  ...dashboardRoutes,
  ...procurementRoutes,
  ...databankRoutes,
  ...eventsRoutes,
  ...logbookRoutes,
  ...accessRoutes,
  ...designationsRoutes,
  ...departmentsRoutes,
  ...logbookDietRoutes,
  ...executiveSetRoutes,
  ...executiveOfficeRoutes,
  ...chapterRoutes,
  ...adminsRoutes,
  ...assessorRoutes,
  ...probationerRoutes,
  // ✅ matches sidebar
  ...settingsRoutes,
  ...comingSoonRoutes,
  ...billingsRoutes,
  ...membershipRoutes,
  ...necOfficeRoutes,
  ...examRoutes,
  ...hrRoutes,
  ...storesRoutes,
  ...committeesRoutes,
  ...committeeReviewRoutes,
  ...upgradeApplicationRoutes,
  ...firmApplicationRoutes,
  ...cpdRoutes,
  ...supportRoutes,
  ...mailRoutes,
  ...nationalBodyRoutes,
  ...forumRoutes,
  ...graduatePledgeRoutes,
];

export default function AppRouter() {
  return (
    <AuthProvider>
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <AuthSync />
        <ImpersonationBanner />
        <GlobalAlerts />

        <Routes>
          {/* PUBLIC ROUTES */}
          {authRoutes.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}

          {/* Open to the internet — no layout, no guard, no session. */}
          {publicEventsRoutes.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}

          {/* The final-year students' pledge form. Same rule: the people it is
              for have no account and must never be sent to /login. */}
          {publicGraduatePledgeRoutes.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}

          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* PROTECTED ROUTES */}
          <Route element={<MainLayout />}>
            {privateRoutes.map((r) => (
              <Route
                key={r.path}
                path={r.path}
                element={
                  // Outermost: a route may offer a public view of itself to
                  // signed-out visitors rather than sending them to login.
                  <GuestRedirect to={(r as any).guestRedirect}>
                    <OnboardingGuard>
                      <RequirePermission roles={(r as any).roles} perms={(r as any).perms}>
                        {r.element}
                      </RequirePermission>
                    </OnboardingGuard>
                  </GuestRedirect>
                }
              />
            ))}
            <Route
              key="profile"
              path="/profile"
              element={
                <OnboardingGuard>
                  <ProfilePage />
                </OnboardingGuard>
              }
            />
          </Route>
          <Route
            key="first_app"
            path="/first-application"
            element={
              <OnboardingGuard>
                <ProbationerApplicationWizard />
              </OnboardingGuard>
            }
          />

          {/* helpers */}
          <Route path="/home" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
